import Header from "../component/Header";
import { useEffect, useRef, useState } from "react";
import { Box, Button, FormControl, TextField, Typography } from "@mui/material";
import RequiredBox from "../component/RequiredBox";
import AlertsDialog from "../component/AlertsDialog";
import AlertsSnackbar from "../component/AlertsSnackbar";
import { useCookies } from "react-cookie";
import { getMyAreaApi, setMyAreaApi } from "../services/api/api";
import { useNavigate } from "react-router-dom";
import MyBottomNavigation from "../component/MyBottomNavigation";
import { useTranslation } from "react-i18next";

function AddMyArea(): JSX.Element {
  const { t } = useTranslation();
  const [cookies, setCookie, removeCookie] = useCookies(['recotech_mapapps_token']);
  const navigate = useNavigate();

  const [isWaitingResponse, setIsWaitingResponse] = useState<boolean>(false);

  const [address, setAddress] = useState<string | null>("");
  const [lat, setLat] = useState<number | null>(0);
  const [lng, setLng] = useState<number | null>(0);
  const [zipCode, setZipCode] = useState<string | null>("");
  const inputZipCodeRef = useRef<HTMLInputElement>(null);
  const [inputZipCodeError, setInputZipCodeError] = useState<boolean>(false);
  const [currentRegion, setCurrentRegion] = useState<string>("jp"); // デフォルトは日本

  const handleZipCodeChange = (value:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // バリデーションを無効化するため、常にエラーなしとする
    setInputZipCodeError(false);
    setZipCode(value.target.value)
  };
  const handleZipCodeButtonClick = async (zipCode:string) => {
    try {
      if(google){
        setAddress("")
        const geocoder = new google.maps.Geocoder();
        // console.log("Searching with region:", currentRegion);
        // 現在のリージョン設定に基づいて API を呼び出す
        const requestOptions: google.maps.GeocoderRequest = { address: zipCode, region: currentRegion };
        if (currentRegion === "MY") {
          requestOptions.componentRestrictions = { country: "MY" };
        } else if (currentRegion === "jp") {
          // 必要であれば日本に限定する設定も追加できますが、現状は region: "jp" のみとします。
          // requestOptions.componentRestrictions = { country: "JP" };
        }
        const response = await geocoder.geocode(requestOptions);
        if (response?.results?.[0]?.formatted_address) {
          const formatted_address_components = response?.results?.[0]?.address_components.reverse().slice(1,-1)
          let address = ""
          formatted_address_components.forEach((address_component) => {
            address += address_component.long_name
          })
          setAddress(address)
          setLat(response?.results?.[0]?.geometry?.location?.lat())
          setLng(response?.results?.[0]?.geometry?.location?.lng())
        }
      }
    } catch (error: any) {
      if(error.code === "ZERO_RESULTS"){
        console.log(error)
        setAlertsMessage(t("pages.addMyArea.zipCodeInvalid"))
        setSeverity("error")
        setOpenSnackbar(true)    
      }else{
        console.log(error)
        setAlertsMessage(t("pages.addMyArea.error"))
        setSeverity("error")
        setOpenSnackbar(true)
      }
    }
  };
  //api情報を読み込みセットする
  const fetchData = async () => {
    if(cookies.recotech_mapapps_token){
      const userArea = await getMyAreaApi(cookies.recotech_mapapps_token,removeCookie);
      if (userArea.zipCode) setZipCode(userArea.zipCode.replace("-", "")) //郵便番号はハイフンを除いた状態で表示
      if (userArea.address) setAddress(userArea.address)
      if (userArea.latitude) setLat(userArea.latitude)
      if (userArea.longitude) setLng(userArea.longitude)
    }
  };

  useEffect(() => {
    if (!cookies.recotech_mapapps_token) navigate("/");
    fetchData();

    // console.log("Attempting to get user location...");
    // 位置情報を取得して国を判定し、リージョンを更新
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        // console.log("Successfully got position:", position.coords);
        const geocoder = new google.maps.Geocoder();
        const latlng = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        try {
          // console.log("Attempting to geocode location:", latlng);
          const response = await geocoder.geocode({ location: latlng });
          // console.log("Geocoding API response:", response);
          if (response.results[0]) {
            const countryComponent = response.results[0].address_components.find(
              (component) => component.types.includes("country")
            );
            // console.log("Found country component:", countryComponent);
            if (countryComponent && countryComponent.short_name === "MY") {
              setCurrentRegion("MY");
              // console.log("Region set to MY");
            } else {
              setCurrentRegion("jp"); // マレーシア以外はデフォルト日本
              // console.log("Region set to jp (not MY or no country component)");
            }
          } else {
            setCurrentRegion("jp"); // 結果なしの場合もデフォルト日本
            // console.log("No results from geocoding, region set to jp");
          }
        } catch (error) {
          console.error("Error getting country from location:", error);
          setCurrentRegion("jp"); // エラー時もデフォルト日本
          // console.log("Error during geocoding, region set to jp");
        }
      }, (error) => {
        console.error("Error getting location:", error);
        setCurrentRegion("jp"); // 位置情報取得失敗時もデフォルト日本
        // console.log("Failed to get location, region set to jp");
      });
    } else {
      // Geolocation APIがサポートされていない場合
      // console.log("Geolocation is not supported by this browser, region set to jp");
      setCurrentRegion("jp"); // デフォルト日本
    }
  }, []);

  //SnackBarの設定
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [alertsMessage, setAlertsMessage] = useState<string>("");
  const [severity, setSeverity] = useState< "error" | "success" >("error");

  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const handleAgreeClick = ( async () => {
    const res = await setMyAreaApi(cookies.recotech_mapapps_token,zipCode!,address!,lat!,lng!,setAlertsMessage,setSeverity,setOpenSnackbar,setIsWaitingResponse)
    if(res){
      //登録後の処理
      navigate('/myarea')
    }else{
      //登録失敗の処理
    }
    setOpenDialog(false)
  })

  const handleSendClick = (() => {
    if(zipCode === ""){
      setAlertsMessage(t("pages.addMyArea.zipCodeFormat"))
      setSeverity("error")
      setOpenSnackbar(true)
      return
    }
    if(address === ""){
      setAlertsMessage(t("pages.addMyArea.addressRequired"))
      setSeverity("error")
      setOpenSnackbar(true)
      return
    }
    setOpenDialog(true)
  })

  useEffect(() => {
    //事前にエリアが設定されていればその情報を表示しておく
  }, []);

  return (
    <>
      <AlertsSnackbar
        openSnackbar={openSnackbar}
        setOpenSnackbar={setOpenSnackbar}
        vertical={"top"}
        severity={severity}
        message={alertsMessage}
      />
      <AlertsDialog
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        handleAgreeClick={()=>handleAgreeClick()}
        dialogTitle={t("pages.addMyArea.title")}
        dialogMessage={t("pages.addMyArea.confirmRegister")}
        isWaitingResponse={isWaitingResponse}
      />
      <Header/>
      <Box 
        sx={{ p: 3 }}
        display="flex"
        alignItems="start"
        justifyContent="start"
        flexDirection="column"
      >
        <Typography component="div" variant="h5" sx={{fontWeight:"bold"}}>
          {t("pages.addMyArea.changeArea")}
        </Typography>
        <Typography component="div" variant="caption">
          　
        </Typography>
        <Typography component="div" variant="caption">
          {t("pages.addMyArea.description")}
        </Typography>

        <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
          <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
            {t("pages.addMyArea.zipCode")}
          </Typography>
          <RequiredBox/>
        </Box>
        <Box display={"flex"} flexDirection={"row"} alignItems={"baseline"} justifyContent={"start"} >
          <FormControl>
            <TextField
              style={{width: "40vw", marginTop: "10px"}}
              variant="outlined"            
              fullWidth={true}
              type={'text'}
              value={zipCode}
              onChange={(value)=>{
                handleZipCodeChange(value)
              }}  
              helperText={
                inputZipCodeError ? t("pages.addMyArea.zipCodeInstruction") : null
              }
              inputProps={{
                inputMode: "numeric", // モバイルデバイス用に数字入力モードを指定する
                maxLength: 7, // 最大7桁にも制限する (maxLengthは残しても良いでしょう)

              }}
              error={inputZipCodeError}
              inputRef={inputZipCodeRef}
            />
          </FormControl>
          <Button
            disabled={(inputZipCodeError || zipCode === "")}
            variant="contained"
            style={{width: "40vw", marginLeft: "20px" ,borderRadius: "20px"}}
            onClick={() => {
              handleZipCodeButtonClick(zipCode!)
            }}
          >
            {t("pages.addMyArea.searchArea")}
          </Button>
        </Box>

        <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
          <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
            {t("pages.addMyArea.myArea")}
          </Typography>
          <RequiredBox/>
        </Box>
        <FormControl>
          <TextField
            disabled={true}
            placeholder={t("pages.addMyArea.autoAssign")}
            style={{width: "85vw", marginTop: "10px"}}
            variant="outlined"            
            fullWidth={true}
            type={'text'}
            value={address}
            onChange={(e)=>{
              setAddress(e.target.value)
            }}  
          />
        </FormControl>

        <Button
          variant="contained"
          style={{width: "85vw", marginTop: "30px" ,backgroundColor: "#4B4B4B", color: "white",borderRadius: "20px"}}
          onClick={() => {
            handleSendClick()
          }}
        >
          {t("pages.addMyArea.register")}
        </Button>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        width="100%"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
        }}
      >
        <MyBottomNavigation />
      </Box>
    </>
  );
};
export default AddMyArea;
